/**
 * Oval Care Sync Service — admin-side API calls for the upstream membership
 * sync outbox (card 842's endpoints, mounted under /api/admin/oval-care-sync).
 *
 * Uses the house `api` client (web/services/api.js) — a hand-rolled `fetch`
 * wrapper, NOT axios. It already attaches the bearer token, refreshes it on a
 * 401, and rejects with an Error carrying `.status`, `.code` and `.data`, which
 * is how the page tells a 409 OVAL_CARE_DISABLED apart from a real failure.
 *
 * ⛔ No response shaping happens here. The controller deliberately never selects
 * `request_payload` / `response_snapshot`, so there is nothing to strip — and a
 * transform in this layer would be a second place for that guarantee to rot.
 */

import api from './api';

const BASE = '/admin/oval-care-sync';

/**
 * Pure. Drop empty / 'all' filters so they never reach the wire — the backend
 * validator treats an unknown-but-present value as a 400, and an empty `status=`
 * would otherwise be sent on every unfiltered load.
 */
const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '' && value !== 'all'
  );
  const query = new URLSearchParams(entries).toString();
  return query ? `?${query}` : '';
};

/** GET the paginated outbox list. `{ items, total, page, pageSize, integrationEnabled }` */
export const listSyncRows = (params = {}) => api.get(`${BASE}${buildQuery(params)}`);

/** GET one row. `{ item, integrationEnabled }` */
export const getSyncRow = (id) => api.get(`${BASE}/${id}`);

/**
 * POST a safe retry. Re-arms the row for the worker; it never dispatches.
 * Rejects with `.status === 409` and `.code === 'OVAL_CARE_DISABLED'` while the
 * integration flag is off — the page surfaces that inline, never as a success.
 */
export const retrySyncRow = (id) => api.post(`${BASE}/${id}/retry`, {});

export default { listSyncRows, getSyncRow, retrySyncRow };
